import React from "react";

const getAttachmentDisplayName = (attachmentDetail) => {
  const preferredName =
    attachmentDetail?.original_name ||
    attachmentDetail?.file_name ||
    attachmentDetail?.name ||
    attachmentDetail?.attachment;

  if (!preferredName) return "Attachment";

  const cleanName = preferredName.split("/").pop()?.split("?")[0] || preferredName;
  return decodeURIComponent(cleanName);
};

const getAttachmentExtension = (fileName = "") => {
  const extension = fileName.split(".").pop();
  if (!extension || extension === fileName) return "";
  return extension.toUpperCase();
};

const getAttachmentCategory = (fileType = "", extension = "") => {
  const normalizedType = fileType.toLowerCase();
  const normalizedExt = extension.toLowerCase();

  if (normalizedType.includes("pdf") || normalizedExt === "pdf") return "pdf";
  if (normalizedType.includes("audio")) return "audio";
  if (
    normalizedType.includes("word") ||
    ["doc", "docx", "odt", "rtf"].includes(normalizedExt)
  ) {
    return "word";
  }
  if (
    normalizedType.includes("sheet") ||
    normalizedType.includes("excel") ||
    ["xls", "xlsx", "csv"].includes(normalizedExt)
  ) {
    return "sheet";
  }
  if (
    normalizedType.includes("presentation") ||
    normalizedType.includes("powerpoint") ||
    ["ppt", "pptx"].includes(normalizedExt)
  ) {
    return "slides";
  }
  if (
    normalizedType.includes("zip") ||
    normalizedType.includes("compressed") ||
    ["zip", "rar", "7z", "tar", "gz"].includes(normalizedExt)
  ) {
    return "archive";
  }
  if (
    normalizedType.includes("json") ||
    normalizedType.includes("text") ||
    ["txt", "json", "md", "xml", "log"].includes(normalizedExt)
  ) {
    return "text";
  }

  return "file";
};

const getAttachmentIconClass = (category) => {
  const icons = {
    pdf: "fa-file-pdf-o",
    audio: "fa-file-audio-o",
    word: "fa-file-word-o",
    sheet: "fa-file-excel-o",
    slides: "fa-file-powerpoint-o",
    archive: "fa-file-archive-o",
    text: "fa-file-text-o",
    file: "fa-file-o",
  };

  return icons[category] || icons.file;
};

const getAttachmentTypeLabel = (category, extension) => {
  const labels = {
    pdf: "PDF document",
    audio: "Audio file",
    word: "Word document",
    sheet: "Spreadsheet",
    slides: "Presentation",
    archive: "Archive",
    text: "Text file",
    file: "File",
  };

  return extension
    ? `${labels[category] || labels.file} (${extension})`
    : labels[category] || labels.file;
};

const getCollageModifier = (count) => {
  if (count <= 1) return "one";
  if (count === 2) return "two";
  if (count === 3) return "three";
  return "many";
};

function MessageAttachments({
  imageAttachments = [],
  nonImageAttachments = [],
  lightboxImages = [],
  onImageClick,
  align = "sender",
}) {
  if (imageAttachments.length === 0 && nonImageAttachments.length === 0) {
    return null;
  }

  const visibleImages = imageAttachments.slice(0, 4);
  const hiddenImageCount = Math.max(imageAttachments.length - 4, 0);
  const collageModifier = getCollageModifier(imageAttachments.length);

  const videoAttachments = nonImageAttachments.filter((attachment) =>
    attachment?.file_type?.includes("video"),
  );
  const fileAttachments = nonImageAttachments.filter(
    (attachment) => !attachment?.file_type?.includes("video"),
  );

  const resourcesUrl = process.env.REACT_APP_CHAT_RESOURCES_URL;

  return (
    <div className={`message__attachments message__attachments--${align}`}>
      {visibleImages.length > 0 && (
        <div
          className={`message__media-frame message__media-frame--${collageModifier}`}
        >
          {visibleImages.map((attachment, index) => {
            const attachmentLink = lightboxImages[index]?.src;
            const fileName = getAttachmentDisplayName(attachment);
            const isOverflowTile =
              hiddenImageCount > 0 && index === visibleImages.length - 1;

            return (
              <button
                key={`image-${attachment.attachment || index}`}
                type="button"
                className="message__media-tile"
                onClick={() => onImageClick?.(lightboxImages, index)}
                aria-label={
                  isOverflowTile
                    ? `View image, ${hiddenImageCount} more`
                    : `View ${fileName}`
                }
              >
                <img src={attachmentLink} alt={fileName} />
                {isOverflowTile && (
                  <span className="message__media-more">+{hiddenImageCount}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {videoAttachments.map((attachment, index) => {
        const fileName = getAttachmentDisplayName(attachment);
        const fileExtension = getAttachmentExtension(fileName);
        const attachmentLink = `${resourcesUrl}/${attachment.attachment}`;

        return (
          <div
            key={`video-${attachment.attachment || index}`}
            className="message__media-video"
          >
            <video controls src={attachmentLink}>
              Your browser does not support the video tag.
            </video>
            <div className="message__media-video-meta">
              <span className="message__item-file-name" title={fileName}>
                {fileName}
              </span>
              <span className="message__item-file-type">
                Video file {fileExtension ? `(${fileExtension})` : ""}
              </span>
            </div>
          </div>
        );
      })}

      {fileAttachments.map((attachment, index) => {
        const attachmentType = attachment.file_type || "";
        const fileName = getAttachmentDisplayName(attachment);
        const fileExtension = getAttachmentExtension(fileName);
        const fileCategory = getAttachmentCategory(attachmentType, fileExtension);
        const attachmentLink = `${resourcesUrl}/${attachment.attachment}`;

        return (
          <div
            key={`file-${attachment.attachment || index}`}
            className="message__item-file-wrap"
          >
            <a
              href={attachmentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="message__item-file-card"
            >
              <span className="message__item-file-icon" aria-hidden="true">
                <i className={`fa ${getAttachmentIconClass(fileCategory)}`} />
              </span>
              <span className="message__item-file-info">
                <span className="message__item-file-name" title={fileName}>
                  {fileName}
                </span>
                <span className="message__item-file-type">
                  {getAttachmentTypeLabel(fileCategory, fileExtension)}
                </span>
              </span>
              <span className="message__item-file-open">Open</span>
            </a>
            {fileCategory === "audio" && (
              <audio
                controls
                className="message__item-file-audio"
                src={attachmentLink}
              >
                Your browser does not support the audio element.
              </audio>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default MessageAttachments;
